import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const testimonials = [
    {
      name: '小雨',
      text: '看了一篇关于自我接纳的文章，第一次觉得有人真的懂我的感受。现在每天练习镜子冥想，慢慢开始喜欢上了不那么完美的自己。',
      date: '2024年10月',
    },
    {
      name: '阿杰',
      text: '和女朋友的关系因为沟通问题差点分手，在网站上读了几篇关于情侣沟通的文章后，我们开始用"我感到……"的方式交流。现在关系比以前更好了。',
      date: '2024年9月',
    },
    {
      name: '默默',
      text: '那句"不喜欢色色的我吗"真的太戳我了。我一直在感情中隐藏真实的自己，看完文章后第一次鼓起勇气和伴侣坦诚地聊了一次。谢谢你们。',
      date: '2024年11月',
    },
    {
      name: 'Leo',
      text: '分手后那段日子每天刷这个网站，那些关于自我重建的文章给了我很大的力量。现在回头看，那段经历真的让我成长了很多。',
      date: '2024年8月',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const honeypot = (formEl.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');
    setFormError('');
    try {
      const fd = new FormData(formEl);
      const res = await fetch('https://readdy.ai/api/form/d9q59gee3oq4jqumtgmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText || '';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('系统检测到异常提交，请稍后再试。');
        } else {
          setFormError(msg || t('about.contact.error'));
        }
        setFormStatus('error');
      }
    } catch {
      setFormError(t('about.contact.error'));
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('about.title'),
    description: t('about.subtitle'),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: t('breadcrumb.home'), href: '/' },
                  { label: t('about.title') },
                ]}
              />
            </div>

            {/* Page Title */}
            <div className="text-center mb-14">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {t('about.title')}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">{t('about.subtitle')}</p>
            </div>

            {/* Brand Story */}
            <section className="mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">{t('about.story')}</h2>
                  <p className="text-foreground-600 leading-relaxed text-sm md:text-base">{t('about.story.content')}</p>
                </div>
                <div className="w-full h-72 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20minimalist%20reading%20nook%20with%20soft%20peach%20and%20cream%20pillows%2C%20a%20wooden%20shelf%20with%20books%2C%20soft%20natural%20morning%20light%20streaming%20through%20a%20window%2C%20peaceful%20and%20healing%20atmosphere%2C%20modern%20editorial%20style%2C%20no%20text%2C%20no%20people&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="心语共鸣品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Mission */}
            <section className="mb-16 bg-background-100 rounded-lg p-8 md:p-10">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">{t('about.mission')}</h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">{t('about.mission.content')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-heart-pulse-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">温暖</h3>
                  <p className="text-sm text-foreground-500">用温暖的态度对待每一种情感</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-lightbulb-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">专业</h3>
                  <p className="text-sm text-foreground-500">以专业的知识提供可靠的内容</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-shield-star-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">真诚</h3>
                  <p className="text-sm text-foreground-500">真诚地面对每一种情感和困惑</p>
                </div>
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">{t('about.reputation')}</h2>
                <p className="text-foreground-500 text-sm md:text-base">{t('about.reputation.desc')}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((item, index) => (
                  <div key={index} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{item.text}&rdquo;</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                      <span className="text-xs text-foreground-400">{item.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="bg-background-100 rounded-lg p-8 md:p-10">
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">{t('about.contact')}</h2>
                <p className="text-foreground-500 text-sm md:text-base">{t('about.contact.desc')}</p>
              </div>
              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="max-w-lg mx-auto"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="mb-4">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('about.contact.name')}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('about.contact.namePlaceholder')}
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('about.contact.email')}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('about.contact.emailPlaceholder')}
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('about.contact.message')}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={t('about.contact.messagePlaceholder')}
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-primary-600 mb-4">{formError}</p>
                )}
                {formStatus === 'success' && (
                  <p className="text-sm text-secondary-600 mb-4">{t('about.contact.success')}</p>
                )}
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-primary-500 text-background-50 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '发送中...' : t('about.contact.submit')}
                </button>
              </form>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}